import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  TextField,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  NoteAdd as NoteAddIcon,
  Pending as PendingIcon,
  People as PeopleIcon,
  Link as LinkIcon,
  Create as CreateIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  AttachMoney as AttachMoneyIcon,
  Timer as TimerIcon,
  Computer as ComputerIcon,
  School as SchoolIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import StudentGetUser from 'utilities/api/StudentAllUserAPIs';
import ImageBaseURL from 'utilities/api/axios';
import { toast } from 'react-toastify';

const progress = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
};

const type = {
  1: 'Academic',
  2: 'Research',
  3: 'Personal',
  4: 'Other',
};

const platform = {
  1: 'Competitions',
  2: 'Exhibitions',
  3: 'Industry',
};
const ProjectDrawer = ({
  drawerWidth,
  drawerOpen,
  toggleDrawer,
  handleSubmit,
  handleDelete,
  currentProject,
}) => {
  const { user } = useSelector((state) => state.user);
  console.log('User:', user);
  const [teammateOptions, setTeammateOptions] = useState([]);
  const [currentTeammates, setCurrentTeammates] = useState([]);
  const [mentorOptions, setMentorOptions] = useState([]);
  const [currentMentors, setCurrentMentors] = useState([]);
  const [creator, setCreator] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [mentorSearchValue, setMentorSearchValue] = useState('');
  const [currentDrawerWidth, setCurrentDrawerWidth] = useState(drawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [teammateError, setTeammateError] = useState('');
  const [mentorError, setMentorError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formik = useFormik({
    initialValues: {
      type: currentProject ? currentProject.type : '',
      progress_status: currentProject ? currentProject.progress_status : '',
      links: currentProject ? currentProject.links : '',
      title: currentProject ? currentProject.title : '',
      description: currentProject ? currentProject.description : '',
      student: user.id,
      platform: currentProject ? currentProject.platform : '',
      expenditure: currentProject ? currentProject.expenditure : '',
      budget: currentProject ? currentProject.budget : '',
      duration: currentProject ? currentProject.duration : '',
      is_patent_filed: currentProject?.is_patent_filed ?? false,
      is_project_visible: currentProject?.is_project_visible ?? true,
      mentors: currentProject ? currentProject.mentors : '',
    },
    onSubmit: async (values) => {
      if (teammateError || mentorError) {
        return;
      }

      try {
        const teammates = currentTeammates.map((teammate) => teammate.id);

        // Convert mentor emails to comma-separated string to match the backend model
        const mentorEmailsString = currentMentors
          .map((mentor) => mentor.email || mentor.username)
          .join(", ");

        // No file, regular JSON submission
        const submissionValues = {
          ...values,
          teammates: teammates,
          mentors: mentorEmailsString // Send as string, not array
        };
        await handleSubmit(submissionValues);
      } catch (error) {
        toast.error('Failed to update project details. Please try again.');
        console.error('Error updating project details:', error);
      }
    },
    enableReinitialize: true,
  });

  // File handling functions
  const handleFileChange = (event) => {
    // Instead of storing the actual file, just store the file name
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0].name);
    }
  };

  // Text field alternative for document URL
  const handleDocumentUrlChange = (event) => {
    setSelectedFile(event.target.value);
  };

  useEffect(() => {
    const fetchUserData = async (username) => {
      try {
        const userData = await StudentGetUser.UserDetailsGet(username);
        return userData[0];
      } catch (error) {
        console.error(`Error fetching data for ${username}:`, error);
        return null;
      }
    };

    const fetchProjectData = async () => {
      if (currentProject) {
        const creatorData = await fetchUserData(currentProject.student);
        setCreator(creatorData);

        const teammates = await Promise.all(
          currentProject.teammates_names.map(async (username) => {
            const teammateData = await fetchUserData(username);
            return teammateData;
          })
        );

        setCurrentTeammates(teammates.filter(Boolean));

        // Process mentors if they exist in the current project
        if (currentProject.mentors && typeof currentProject.mentors === 'string') {
          // Handle the case where mentors might be stored as comma-separated string
          const mentorNames = currentProject.mentors.split(',').map(name => name.trim());
          const mentors = await Promise.all(
            mentorNames.map(async (mentorName) => {
              try {
                const mentorData = await StudentGetUser.UserDetailsGet(mentorName);
                return mentorData[0];
              } catch (error) {
                return null;
              }
            })
          );
          setCurrentMentors(mentors.filter(Boolean));
        } else if (Array.isArray(currentProject.mentors)) {
          // Handle the case where mentors might be an array of objects or IDs
          const mentors = await Promise.all(
            currentProject.mentors.map(async (mentor) => {
              const mentorUsername = typeof mentor === 'object' ? mentor.username : mentor;
              try {
                const mentorData = await StudentGetUser.UserDetailsGet(mentorUsername);
                return mentorData[0];
              } catch (error) {
                return null;
              }
            })
          );
          setCurrentMentors(mentors.filter(Boolean));
        }

        formik.setValues({
          type: currentProject.type || '',
          progress_status: currentProject.progress_status || '',
          links: currentProject.links || '',
          title: currentProject.title || '',
          description: currentProject.description || '',
          student: currentProject.student || user.id,
          platform: currentProject.platform || '',
          expenditure: currentProject.expenditure || '',
          budget: currentProject.budget || '',
          duration: currentProject.duration || '',
          is_patent_filed: currentProject.is_patent_filed ?? false,
          is_project_visible: currentProject.is_project_visible ?? true,
          mentors: '',  // We'll handle mentors separately through currentMentors state
        });
      } else if (!currentProject) {
        formik.resetForm();
        setCurrentTeammates([]);
        setCurrentMentors([]);
        setCreator(null);
        setSelectedFile(null);
      }
    };

    fetchProjectData();
  }, [currentProject, user.id]);

  const handleTeammateSearch = async (event, value) => {
    setSearchValue(value);

    if (value.length > 0) {
      try {
        const results = await StudentGetUser.UserDetailsGet(value);
        const filteredResults = results.filter((users) => users.TL_name === user.TL_name);
        setTeammateOptions(filteredResults);
      } catch (error) {
        console.error('Error fetching teammate options:', error);
      }
    } else {
      setTeammateOptions([]);
    }
  };

  const handleMentorSearch = async (event, value) => {
    setMentorSearchValue(value);

    if (value.length > 0) {
      try {
        const results = await StudentGetUser.UserDetailsGet(value);
        // Filter to only users that have email addresses
        const usersWithEmail = results.filter(user => user.email);
        setMentorOptions(usersWithEmail);
      } catch (error) {
        console.error('Error fetching mentor options:', error);
      }
    } else {
      setMentorOptions([]);
    }
  };

  const handleTeammateSelect = (event, value) => {
    const newTeammates = value.filter(
      (teammate) =>
        !currentTeammates.some((current) => current.id === teammate.id) &&
        teammate.id !== formik.values.student
    );

    if (newTeammates.length !== value.length) {
      setTeammateError('Some teammates are already added or are the project owner.');
    } else {
      setTeammateError('');
    }

    const updatedTeammates = [...currentTeammates, ...newTeammates];

    setCurrentTeammates(updatedTeammates);
    setTeammateOptions([]);
    setSearchValue('');
  };

  const handleMentorSelect = (event, value) => {
    const newMentors = value.filter(
      (mentor) => !currentMentors.some((current) => current.id === mentor.id)
    );

    if (newMentors.length !== value.length) {
      setMentorError('Some mentors are already added.');
    } else {
      setMentorError('');
    }

    const updatedMentors = [...currentMentors, ...newMentors];

    setCurrentMentors(updatedMentors);
    setMentorOptions([]);
    setMentorSearchValue('');
  };

  const handleRemoveTeammate = (teammateToRemove) => {
    setCurrentTeammates(currentTeammates.filter((teammate) => teammate.id !== teammateToRemove.id));
    setTeammateError('');
  };

  const handleRemoveMentor = (mentorToRemove) => {
    setCurrentMentors(currentMentors.filter((mentor) => mentor.id !== mentorToRemove.id));
    setMentorError('');
  };

  const handleMouseDown = (e) => {
    if (!isMobile) {
      setIsResizing(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isResizing) {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 300) {
        setCurrentDrawerWidth(newWidth);
      }
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing && !isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isMobile]);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={drawerOpen}
      onClose={() => toggleDrawer(false)}
      sx={{ zIndex: 101 }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : currentDrawerWidth,
          height: isMobile ? 'auto' : '100vh',
        },
      }}
    >
      <Box
        sx={{
          height: isMobile ? 'auto' : '100%',
          width: '100%',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
        role="presentation"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isMobile ? '16px' : '16px 24px',
            backgroundColor: '#2c387e',
            color: 'white',
          }}
        >
          <Typography variant="h6">{currentProject ? 'Edit Project' : 'Add Project'}</Typography>
          <Button onClick={() => toggleDrawer(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </Button>
        </Box>
        <Box sx={{ padding: 2, flexGrow: 1, overflowY: 'auto' }}>
          <form onSubmit={formik.handleSubmit}>
            <List>
              {/* Project Title */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <CreateIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  placeholder="Add Project Title *"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  name="title"
                />
              </ListItem>

              {/* Project Description */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                  height: '130px',
                }}
              >
                <DescriptionIcon />
                <TextField
                  variant="outlined"
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '100px', // Set the height to 100px
                      alignItems: 'flex-start', // Align text to top
                    },
                    '& .MuiInputBase-inputMultiline': {
                      height: '100% !important', // Make the input area take full height
                    }
                  }}
                  placeholder="Add Project Description *"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  name="description"
                  multiline
                  rows={4}
                  inputProps={{ 
                    maxLength: 250
                  }}
                  helperText={`${formik.values.description.length}/250 characters`}
                  FormHelperTextProps={{
                    sx: { 
                      textAlign: 'right',
                      marginRight: 0
                    }
                  }}
                />
              </ListItem>

              {/* Project Type */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <NoteAddIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="type-select">Type of Project *</InputLabel>
                  <Select
                    label="Type of Project"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    {Object.entries(type).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              {/* Project Progress Status */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <PendingIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="progress-status-select">Project Progress Status *</InputLabel>
                  <Select
                    label="Project Progress Status"
                    name="progress_status"
                    value={formik.values.progress_status}
                    onChange={formik.handleChange}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    {Object.entries(progress).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              {/* Project Platform */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <ComputerIcon />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel htmlFor="platform-select">Project Platform</InputLabel>
                  <Select
                    label="Project Platform"
                    name="platform"
                    value={formik.values.platform}
                    onChange={formik.handleChange}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.entries(platform).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              {/* Budget and Expenditure */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <AttachMoneyIcon />
                <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                  <TextField
                    variant="outlined"
                    sx={{ width: '50%' }}
                    label="Budget"
                    type="number"
                    value={formik.values.budget}
                    onChange={formik.handleChange}
                    name="budget"
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                  <TextField
                    variant="outlined"
                    sx={{ width: '50%' }}
                    label="Expenditure"
                    type="number"
                    value={formik.values.expenditure}
                    onChange={formik.handleChange}
                    name="expenditure"
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Box>
              </ListItem>

              {/* Duration */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <TimerIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  label="Duration (months)"
                  type="number"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  name="duration"
                  InputProps={{
                    inputProps: { min: 0, step: 0.1 },
                  }}
                />
              </ListItem>
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                {/* Patent Filed */}
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.is_patent_filed}
                        onChange={(e) => formik.setFieldValue('is_patent_filed', e.target.checked)}
                        name="is_patent_filed"
                        color="warning"
                      />
                    }
                    label="Patent Filed"
                  />
                </ListItem>

                {/* Project Visibility */}
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.is_project_visible}
                        onChange={(e) => formik.setFieldValue('is_project_visible', e.target.checked)}
                        name="is_project_visible"
                        color="warning"
                      />
                    }
                    label="Project Visible"
                  />
                </ListItem>
              </ListItem>
              {/* Mentors */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <SchoolIcon sx={{ mr: '4px' }} />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <Autocomplete
                    multiple
                    options={mentorOptions}
                    getOptionLabel={(option) =>
                      `${option.name || option.username} (${option.email || option.username})`
                    }
                    filterSelectedOptions
                    onInputChange={handleMentorSearch}
                    onChange={handleMentorSelect}
                    value={currentMentors}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        height: 'auto',
                        padding: '4px',
                      },
                      '& .MuiChip-root': {
                        margin: '4px',
                      },
                      '& .MuiInputBase-root': {
                        display: 'flex',
                        flexWrap: 'wrap',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Search and Add Mentors (email required)"
                        sx={{ width: '100%' }}
                      />
                    )}
                  />
                </FormControl>
              </ListItem>
              {mentorError && (
                <ListItem>
                  <Alert severity="error">{mentorError}</Alert>
                </ListItem>
              )}
              {currentMentors.length > 0 && (
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ marginRight: 2 }}>
                    Current Mentors:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentMentors.map((mentor) => (
                      <Chip
                        key={mentor.id}
                        label={`${mentor.name || mentor.username} (${mentor.email || mentor.username})`}
                        avatar={
                          <Avatar
                            src={
                              mentor.profile_pic
                                ? `${ImageBaseURL.defaults.baseURL}${mentor.profile_pic}`
                                : ''
                            }
                          />
                        }
                        onDelete={() => handleRemoveMentor(mentor)}
                        deleteIcon={<DeleteIcon />}
                        sx={{ margin: '4px 0' }}
                      />
                    ))}
                  </Box>
                </ListItem>
              )}

              {/* Documents Upload */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <CloudUploadIcon />
                <Box sx={{ width: '100%' }}>
                  <TextField
                    variant="outlined"
                    sx={{ width: '100%' }}
                    placeholder="Enter document URL or upload file"
                    value={selectedFile || ''}
                    onChange={handleDocumentUrlChange}
                    name="documents"
                  />
                </Box>
              </ListItem>

              {/* Links */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: 2,
                }}
              >
                <LinkIcon />
                <TextField
                  variant="outlined"
                  sx={{ width: '100%' }}
                  placeholder="Add Links (comma-separated URLs)"
                  value={formik.values.links}
                  onChange={formik.handleChange}
                  name="links"
                />
              </ListItem>

              {/* Creator */}
              {creator && (
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 2,
                  }}
                >
                  <PeopleIcon sx={{ mr: '4px' }} />
                  <Typography variant="body2" sx={{ marginRight: 2 }}>
                    Creator:
                  </Typography>
                  <Chip
                    avatar={
                      <Avatar
                        src={
                          creator?.profile_pic
                            ? `${ImageBaseURL.defaults.baseURL}${creator.profile_pic}`
                            : ''
                        }
                      />
                    }
                    label={`${creator?.name || creator?.username} (${creator?.username})`}
                  />
                </ListItem>
              )}
              <input type="hidden" name="student" value={formik.values.student} />

              {/* Teammates */}
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <PeopleIcon sx={{ mr: '4px' }} />
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <Autocomplete
                    multiple
                    options={teammateOptions}
                    getOptionLabel={(option) => `${option.name || option.username} (${option.username})`}
                    filterSelectedOptions
                    onInputChange={handleTeammateSearch}
                    onChange={handleTeammateSelect}
                    value={currentTeammates}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        height: 'auto',
                        padding: '4px',
                      },
                      '& .MuiChip-root': {
                        margin: '4px',
                      },
                      '& .MuiInputBase-root': {
                        display: 'flex',
                        flexWrap: 'wrap',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Search and Add Teammates"
                        sx={{ width: '100%' }}
                      />
                    )}
                  />
                </FormControl>
              </ListItem>
              {teammateError && (
                <ListItem>
                  <Alert severity="error">{teammateError}</Alert>
                </ListItem>
              )}
              {currentTeammates.length > 0 && (
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ marginRight: 2 }}>
                    Current Teammates:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentTeammates.map((teammate) => (
                      <Chip
                        key={teammate.id}
                        label={`${teammate.name || teammate.username} (${teammate.username})`}
                        avatar={
                          <Avatar
                            src={
                              teammate.profile_pic
                                ? `${ImageBaseURL.defaults.baseURL}${teammate.profile_pic}`
                                : ''
                            }
                          />
                        }
                        onDelete={() => handleRemoveTeammate(teammate)}
                        deleteIcon={<DeleteIcon />}
                        sx={{ margin: '4px 0' }}
                      />
                    ))}
                  </Box>
                </ListItem>
              )}
            </List>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'end',
                gap: '8px',
                marginTop: '4rem',
                marginRight: isMobile ? '0' : '2rem',
              }}
            >
              {currentProject && (
                <Button
                  variant="outlined"
                  color="warning"
                  sx={{
                    minHeight: '48px',
                    textTransform: 'none',
                    borderRadius: '8px',
                    border: '2px solid #212121',
                    fontWeight: 600,
                    width: isMobile ? '100%' : '25%',
                    color: '#C90505',
                  }}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="warning"
                sx={{
                  minHeight: '48px',
                  textTransform: 'none',
                  backgroundColor: '#E79A2B',
                  borderRadius: '8px',
                  border: '2px solid #212121',
                  fontWeight: 600,
                  width: isMobile ? '100%' : '25%',
                }}
              >
                {currentProject ? 'Save' : 'Add'}
              </Button>
            </Box>
          </form>
        </Box>
        {!isMobile && (
          <Box
            sx={{
              width: '5px',
              backgroundColor: '#ddd',
              cursor: 'ew-resize',
              height: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 102,
            }}
            onMouseDown={handleMouseDown}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default ProjectDrawer;
